import { EFileUploadStatus } from "@frontend-shared/types";
import { AlertCircle, Loader2, X } from "lucide-react";
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import type { IFolderKnowledgeUploadRowProps } from "./types";

// NOTE: One in-flight or failed upload in a chat folder's knowledge panel.
// NSAI-7817: a failed row now says why (over the size cap, connection dropped,
// the server's reason) instead of a bare red X, and a file waiting for a free
// upload slot says so instead of sitting at 0%.
const FolderKnowledgeUploadRowInner: React.FC<
  IFolderKnowledgeUploadRowProps
> = ({ entry, onDismiss }) => {
  const { t } = useTranslation("knowledge");
  const isUploading = entry.status === EFileUploadStatus.UPLOADING;

  const handleDismiss = useCallback(
    (): void => onDismiss(entry.id),
    [entry.id, onDismiss],
  );

  return (
    <div
      className="rounded-md border border-border px-3 py-2 text-sm"
      data-testid={`folder-knowledge-upload-${entry.id}`}
    >
      <div className="flex items-center gap-2">
        {isUploading ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <X className="h-4 w-4 shrink-0 text-destructive" />
        )}
        <span className="flex-1 truncate">{entry.fileName}</span>
        {isUploading ? (
          <span
            className="shrink-0 text-xs text-muted-foreground"
            data-testid={`folder-knowledge-upload-progress-${entry.id}`}
          >
            {entry.isQueued ? t("uploadQueued") : `${entry.progress}%`}
          </span>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={handleDismiss}
            data-testid={`folder-knowledge-upload-dismiss-${entry.id}`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {!isUploading && entry.errorMessage && (
        <p
          className="mt-1 flex items-start gap-1 text-xs text-destructive break-words"
          data-testid={`folder-knowledge-upload-error-${entry.id}`}
        >
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="break-words">{entry.errorMessage}</span>
        </p>
      )}
    </div>
  );
};

export const FolderKnowledgeUploadRow = memo(FolderKnowledgeUploadRowInner);
